/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Grid, Col, Row } from 'react-bootstrap';
import { get, isEqual, max } from 'lodash';
import numeral from 'numeral';

import ComponentHeader from '../../components/Headers/component-header';
import InlineQuery from '../../components/InlineQuery';
import AudienceRatings from '../../components/AudienceRatings';
import { truncateText } from '../../utils/shared';
import { extractFilterValues } from '../../utils/filters';
import moviesConfig from '../configs/movies-config';
import { FilterTypes } from '../../components/Filters';
import {
  CountryDemo,
  AgeDemo,
  GenderDemo
} from '../../components/Charts/DemoCharts';
import Table from '../../components/Table';
import { downloadReportFunc } from '../../utils/export';
import ModuleExport from '../../components/ModuleExport';
import {
  audienceWAVWColumnsDefaultSorts,
  audienceWAVWColumns,
  formatGenderDemo
} from './table-config';
import { formatAudienceRatings } from '../../utils/movie';
import { sortPlatforms } from '../../utils/shared';

import './styles/MovieInsights.css';

const MovieInsights = ({
  movie,
  queries: {
    movieRating,
    movieTrackerAgeIndex,
    movieTrackerGenderIndex,
    movieTrackerCountryIndex,
    movieAudiencePlatform
  },
  queryActions: { runFormattedInlineQueriesForPage },
  updateBenchmarkData,
  filters,
  blocked
}) => {
  const [selectedFilters, setSelectedFilters] = useState({});

  const updateQueries = () => {
    runFormattedInlineQueriesForPage(
      moviesConfig.MOVIE_REPORT_FILTERS[moviesConfig.MOVIE_VIEWERSHIP_PAGE_ID],
      filters.fields
    );
    updateBenchmarkData(filters.fields, null, true);
  };

  useEffect(() => {
    if (blocked) {
      return;
    }

    const values = extractFilterValues(filters.fields);
    const hasNewValues = !isEqual(values, selectedFilters);
    if (hasNewValues) {
      setSelectedFilters(values);
      updateQueries();
    }
  }, [blocked, filters.fields]);

  return (
    <Grid fluid componentClass="section">
      <Row>
        <Col xs={12} md={6}>
          <Col xs={12} className="movie-report-demo-col">
            <div className="movie_report__audience-ratings-wrapper">
              <ComponentHeader title="Audience Ratings" />
              <ModuleExport
                fetchData
                tableDataDownload={downloadReportFunc(
                  'movieRating',
                  filters.fields,
                  moviesConfig.MOVIE_REPORT_FILTERS.movieReportViewership
                    .queries.movieRating
                )}
                showTitle={get(movie, 'name', '')}
                csv
                xls
                png
              />
              <InlineQuery useChildForLoading query={movieRating} smaller>
                {query => (
                  <AudienceRatings
                    loading={query.loading}
                    {...formatAudienceRatings(query)}
                  />
                )}
              </InlineQuery>
            </div>
            <div className="movie-report-section-wrapper-wavw">
              <ComponentHeader
                title={<span>Where Are Viewers Watching</span>}
                tooltip="Displays platforms where TV Time users indicated they watched the Title"
              />

              <InlineQuery useChildForLoading query={movieAudiencePlatform}>
                {query => {
                  return (
                    <Table
                      data={sortPlatforms(query.data)}
                      headerFormatter={audienceWAVWColumns}
                      defaultSorts={audienceWAVWColumnsDefaultSorts}
                      view="movieAudiencePlatform"
                      filters={filters.fields}
                      showRank={false}
                      queryMeta={
                        moviesConfig.MOVIE_REPORT_FILTERS.movieReportViewership
                          .queries.movieAudiencePlatform
                      }
                      titleName={movie.name}
                      dataSize={query.data.reduce((memo, current) => {
                        return current.sampleSize;
                      }, 0)}
                      width={563}
                      height={546}
                    />
                  );
                }}
              </InlineQuery>
            </div>
            <div className="show_report__demo-chart-wrapper">
              <ComponentHeader
                title={
                  <span>
                    Age of <em>{truncateText(get(movie, 'name', ''))}</em>{' '}
                    Viewers
                  </span>
                }
                tooltip="This metric represents a normalized view of
                this defined audience. It is calculated
                relative to the overall app population to
                adjust for app population biases. The
                percentages represented do not reflect raw
                viewer demographics."
              />
              <ModuleExport
                fetchData
                tableDataDownload={downloadReportFunc(
                  'movieTrackerAgeIndex',
                  filters.fields,
                  moviesConfig.MOVIE_REPORT_FILTERS.movieReportViewership
                    .queries.movieTrackerAgeIndex,
                  'age'
                )}
                showTitle={get(movie, 'name', '')}
                csv
                xls
                png
              />
              <div className="tvt-movie-report__audience-ageDemo-chart-wrapper">
                <AgeDemo
                  query={movieTrackerAgeIndex}
                  layout="vertical"
                  sortKey="age"
                  indexKey="age"
                  width={570}
                  keys={['index']}
                  topTooltipLabel="% Viewers"
                  bottomTooltipLabel="Viewers"
                  tooltipFormatter={({ value }) =>
                    `${numeral(value * 100).format('0.0')}%`
                  }
                />
              </div>
            </div>
          </Col>
        </Col>
        <Col xs={12} md={6} className="show_report__demo-chart-wrapper">
          <Col xs={12} className="movie-report-demo-row">
            <ComponentHeader
              title={
                <span>
                  Country Index of{' '}
                  <em>{truncateText(get(movie, 'name', ''), 80)}</em> Viewers
                </span>
              }
              tooltip={
                "Index measures a particular group's preferences relative to the overall TV Time audience."
              }
            />
            <ModuleExport
              fetchData
              tableDataDownload={downloadReportFunc(
                'movieTrackerCountryIndex',
                filters.fields,
                moviesConfig.MOVIE_REPORT_FILTERS.movieReportViewership.queries
                  .movieTrackerCountryIndex,
                'age'
              )}
              showTitle={get(movie, 'name', '')}
              csv
              xls
              png
            />
            <div className="tvt-movie-report__audience-countryDemo-chart-wrapper">
              <CountryDemo
                maxValue={max(
                  movieTrackerCountryIndex.data.map(c => Math.abs(c.index))
                )}
                query={movieTrackerCountryIndex}
                keys={['index']}
                indexKey="country"
                valueKey="index"
              />
            </div>
          </Col>
          <Col xs={12} className="movie-report-demo-row">
            <div className="show_report__demo-chart-wrapper">
              <ComponentHeader
                title={
                  <span>
                    Gender of <em>{truncateText(get(movie, 'name', ''))}</em>{' '}
                    Viewers
                  </span>
                }
                tooltip="This metric represents a normalized view of
                this defined audience. It is calculated
                relative to the overall app population to
                adjust for app population biases. The
                percentages represented do not reflect raw
                viewer demographics."
              />
              <ModuleExport
                fetchData
                tableDataDownload={downloadReportFunc(
                  'movieTrackerGenderIndex',
                  filters.fields,
                  moviesConfig.MOVIE_REPORT_FILTERS.movieReportViewership
                    .queries.movieTrackerGenderIndex,
                  'age'
                )}
                showTitle={get(movie, 'name', '')}
                csv
                xls
                png
              />
              <div className="tvt-movie-report__audience-genderDemo-chart-wrapper">
                <GenderDemo
                  id="show_view_gender_index.viewer_gender"
                  label="gender"
                  value="index"
                  layout="vertical"
                  width={570}
                  height={293}
                  topTooltipLabel="% Viewers"
                  bottomTooltipLabel="Viewers"
                  query={{
                    loading: movieTrackerGenderIndex.loading,
                    data: formatGenderDemo(movieTrackerGenderIndex)
                  }}
                />
              </div>
            </div>
          </Col>
        </Col>
      </Row>
      {/* <Row>
        <Col xs={12}>
          <ComponentHeader title="Audience Ratings" />
          <InlineQuery query={movieRating} smaller>
            {query => <AudienceRatings {...formatAudienceRatings(query)} />}
          </InlineQuery>
        </Col>
      </Row>

      <Row>
        <Col xs={6} className="movie-report-demo-col">
          <ComponentHeader
            title={
              <span>
                Age of <em>{truncateText(get(movie, 'name', ''))}</em> Viewers
              </span>
            }
            tooltip="Viewer Age Distribution is normalized for biases in TV Time panel."
          />
          <ModuleExport
            fetchData
            tableDataDownload={downloadReportFunc(
              'movieTrackerAgeIndex',
              filters.fields,
              moviesConfig.MOVIE_REPORT_FILTERS.movieReportViewership.queries
                .movieTrackerAgeIndex,
              'age'
            )}
            csv
            xls
            png
          />
          <div className="tvt-movie-report__audience-ageDemo-chart-wrapper">
            <AgeDemo
              query={movieTrackerAgeIndex}
              layout="vertical"
              sortKey="age"
              indexKey="age"
              width={570}
              keys={['index']}
              tooltipFormatter={({ value }) =>
                `${numeral(value * 100).format('0.0')}%`
              }
            />
          </div>
        </Col>
        <Col xs={6} className="movie-report-demo-col">
          <ComponentHeader
            title={
              <span>
                Gender of <em>{truncateText(get(movie, 'name', ''))}</em>{' '}
                Viewers
              </span>
            }
            tooltip="Gender Distribution is normalized for biases in TV Time panel."
          />
          <ModuleExport
            fetchData
            tableDataDownload={downloadReportFunc(
              'movieTrackerGenderIndex',
              filters.fields,
              moviesConfig.MOVIE_REPORT_FILTERS.movieReportViewership.queries
                .movieTrackerGenderIndex,
              'age'
            )}
            csv
            xls
            png
          />
          <div className="tvt-movie-report__audience-genderDemo-chart-wrapper">
            <GenderDemo
              id="show_view_gender_index.viewer_gender"
              label="gender"
              value="index"
              layout="vertical"
              width={570}
              height={293}
              query={{
                loading: movieTrackerGenderIndex.loading,
                data: formatGenderDemo(movieTrackerGenderIndex)
              }}
            />
          </div>
        </Col>
      </Row>
      <Row className="movie-report-demo-row">
        <Col xs={12}>
          <ComponentHeader
            title={
              <span>
                Country Index of{' '}
                <em>{truncateText(get(movie, 'name', ''), 80)}</em> Viewers
              </span>
            }
            tooltip={
              "Index measures a particular group's preferences relative to the overall TV Time audience."
            }
          />
          <ModuleExport
            fetchData
            tableDataDownload={downloadReportFunc(
              'movieTrackerCountryIndex',
              filters.fields,
              moviesConfig.MOVIE_REPORT_FILTERS.movieReportViewership.queries
                .movieTrackerCountryIndex,
              'age'
            )}
            csv
            xls
            png
          />
          <div className="tvt-movie-report__audience-countryDemo-chart-wrapper">
            <CountryDemo
              maxValue={max(
                movieTrackerCountryIndex.data.map(c => Math.abs(c.index))
              )}
              query={movieTrackerCountryIndex}
              keys={['index']}
              indexKey="country"
              valueKey="index"
            />
          </div>
        </Col>
      </Row> */}
    </Grid>
  );
};

MovieInsights.propTypes = {
  movie: PropTypes.shape({
    name: PropTypes.string.isRequired
  }).isRequired,
  queries: PropTypes.shape({
    movieFavorites: PropTypes.shape({}).isRequired,
    movieRating: PropTypes.shape({}).isRequired,
    movieMobility: PropTypes.shape({}).isRequired,
    movieTrackerAgeIndex: PropTypes.shape({}).isRequired,
    movieTrackerGenderIndex: PropTypes.shape({}).isRequired,
    movieTrackerCountryIndex: PropTypes.shape({}).isRequired
  }).isRequired,
  updateBenchmarkData: PropTypes.func.isRequired,

  onIframeLoad: PropTypes.func,
  queryActions: PropTypes.shape({
    runFormattedInlineQueriesForPage: PropTypes.func.isRequired
  }).isRequired,

  blocked: PropTypes.bool.isRequired,
  filters: PropTypes.shape({
    fields: FilterTypes.FilterSetType, // eslint-disable-line react/no-typos
    groupings: FilterTypes.GroupingSetType // eslint-disable-line react/no-typos
  }).isRequired
};

MovieInsights.defaultProps = {
  onIframeLoad: () => {}
};

export default MovieInsights;
